import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { CaseAgeBucket, OwnerTeam, TeamAgeBucket } from '../../services/homeStats';
import { OverviewCard } from '../billing/overview/OverviewCard';
import { DonutChart, SliceLegend, type PieSlice } from '../monitoring/overview/OverviewDonut';
import { rankedSliceTone, type SliceTone } from '../monitoring/overview/overviewLegend';
import { ChartSkeleton } from '../shared/ChartSkeleton';
import { TeamFlowChart, TeamTrendChart } from './HomeCharts';
import {
    FLOW_HEX,
    OTHER_STEPS_KEY,
    teamColor,
    type StepSlice,
    type TeamFlow,
    type TeamWorkload
} from './teamStatsModel';

const CARD_INFO =
    'Open cases whose current step this team owns. The change and trend cover the selected range and are reconstructed from case-flow history, so read them as directional. Time in step is time in the current step; by step shows the five busiest steps, the rest as Other. In vs treated counts, per bucket, cases entering the team and cases it hands on.';
const FLOW_UNAVAILABLE = 'Trend and in vs treated need BigQuery analytics, not connected here.';
const SKELETON_HEIGHT_PX = 240;

const AGE_LABELS: Record<CaseAgeBucket, string> = {
    under1d: '<1d',
    '1to3d': '1–3d',
    '3to7d': '3–7d',
    over7d: '>7d'
};
// Younger buckets darken with age; the stale >7d bucket breaks the ramp in amber.
const AGE_TONES: Record<CaseAgeBucket, SliceTone> = {
    under1d: { swatch: 'bg-slate-300', fill: 'fill-slate-300' },
    '1to3d': { swatch: 'bg-slate-500', fill: 'fill-slate-500' },
    '3to7d': { swatch: 'bg-slate-700', fill: 'fill-slate-700' },
    over7d: { swatch: 'bg-amber-500', fill: 'fill-amber-500' }
};
const OTHER_STEPS_TONE: SliceTone = { swatch: 'bg-gray-400', fill: 'fill-gray-400' };
const NO_OPEN_CASES = 'No open cases.';
const PIE_SIZE_PX = 80;
// Sized so three pies share one row on an xl card; narrower cards wrap the third.
const PIE_ROW_CLASSES = 'grid grid-cols-[repeat(auto-fit,minmax(5rem,1fr))] gap-x-3 gap-y-4';
// Header plus four sections; subgrid rows line each section up across the cards in a grid row.
const CARD_GRID_CLASSES = 'grid grid-cols-1 grid-rows-subgrid row-span-5 gap-y-1';
const CONTENT_GRID_CLASSES = 'grid grid-cols-1 grid-rows-subgrid row-span-4 gap-y-4 px-5 pb-5';

function teamQueueUrl(team: OwnerTeam): string {
    return `/work-queue?team=${encodeURIComponent(team)}`;
}

function TeamTitle({ team }: { team: OwnerTeam }) {
    return (
        <Link
            to={teamQueueUrl(team)}
            title={`Open ${team} cases in the Work Queue`}
            className="inline-flex items-center gap-1.5 hover:underline"
        >
            <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: teamColor(team) }}
            />
            {team}
        </Link>
    );
}

function SectionLabel({ children }: { children: string }) {
    return (
        <p className="text-[10.5px] font-bold uppercase tracking-wider text-gray-400">{children}</p>
    );
}

export function MutedNote({ children }: { children: string }) {
    return <p className="py-3 text-center text-xs text-gray-400">{children}</p>;
}

// Growing queue red, shrinking green: the same convention as the in vs treated chart.
function ChangeBadge({ change }: { change: number }) {
    if (change === 0) return <span className="text-xs text-gray-400">flat in range</span>;
    const isUp = change > 0;
    return (
        <span
            className={`text-xs font-semibold tabular-nums ${isUp ? 'text-red-600' : 'text-green-600'}`}
        >
            {isUp ? '▲ +' : '▼ '}
            {change} in range
        </span>
    );
}

function ageSlices(buckets: TeamAgeBucket[]): PieSlice[] {
    return buckets.map((b) => ({
        key: b.bucket,
        label: AGE_LABELS[b.bucket],
        count: b.open,
        ...AGE_TONES[b.bucket]
    }));
}

function stepSlices(steps: StepSlice[]): PieSlice[] {
    return steps.map((step, rank) => ({
        ...step,
        ...(step.key === OTHER_STEPS_KEY ? OTHER_STEPS_TONE : rankedSliceTone(rank))
    }));
}

interface PieBlockProps {
    title: string;
    slices: PieSlice[];
    emptyText: string;
    /** Makes the section label a link. */
    href?: string;
}

export function PieBlock({ title, slices, emptyText, href }: PieBlockProps) {
    const label = <SectionLabel>{title}</SectionLabel>;
    return (
        <div className="flex min-w-0 flex-col gap-2">
            {href ? (
                <Link to={href} className="hover:underline">
                    {label}
                </Link>
            ) : (
                label
            )}
            {slices.every((slice) => slice.count === 0) ? (
                <MutedNote>{emptyText}</MutedNote>
            ) : (
                <>
                    <div className="self-center">
                        <DonutChart slices={slices} label={title} size={PIE_SIZE_PX} tooltipBelow />
                    </div>
                    <SliceLegend slices={slices} compact />
                </>
            )}
        </div>
    );
}

function FlowTotal({ label, value, color }: { label: string; value: number; color: string }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 shrink-0 rounded-[2px]" style={{ background: color }} />
            {label}
            <span className="font-semibold tabular-nums text-gray-900">{value}</span>
        </span>
    );
}

function FlowSlot({ flow }: { flow: TeamFlow }) {
    if (flow.inflow === 0 && flow.throughput === 0) {
        return <MutedNote>Nothing in or treated in this range.</MutedNote>;
    }
    return (
        <>
            <div className="flex items-center gap-3 text-[12px] text-gray-500">
                <FlowTotal label="In" value={flow.inflow} color={FLOW_HEX.inflow} />
                <FlowTotal label="Treated" value={flow.throughput} color={FLOW_HEX.throughput} />
            </div>
            <TeamFlowChart points={flow.series} />
        </>
    );
}

function TrendSlot({ flow, color }: { flow: TeamFlow | null; color: string }) {
    if (!flow) return <MutedNote>{FLOW_UNAVAILABLE}</MutedNote>;
    if (flow.trend.length === 0) return <MutedNote>No backlog history in this range.</MutedNote>;
    return <TeamTrendChart points={flow.trend} color={color} />;
}

interface TeamWorkloadCardProps {
    workload: TeamWorkload;
    /** A team-specific PieBlock appended to the pie row. */
    extraPie?: ReactNode;
}

export function TeamWorkloadCard({ workload, extraPie }: TeamWorkloadCardProps) {
    const { team, open, ageBuckets, steps, flow } = workload;
    const color = teamColor(team);
    return (
        <OverviewCard
            title={<TeamTitle team={team} />}
            info={CARD_INFO}
            noPadding
            className={CARD_GRID_CLASSES}
            contentClassName={CONTENT_GRID_CLASSES}
        >
            <div className="flex items-baseline gap-2">
                <span className="text-[26px] font-bold leading-none tabular-nums text-gray-900">
                    {open}
                </span>
                <span className="text-sm text-gray-500">open</span>
                {flow && flow.change !== null && <ChangeBadge change={flow.change} />}
            </div>

            <TrendSlot flow={flow} color={color} />

            <div className={PIE_ROW_CLASSES}>
                <PieBlock
                    title="Time in step"
                    slices={ageSlices(ageBuckets)}
                    emptyText={NO_OPEN_CASES}
                />
                <PieBlock title="By step" slices={stepSlices(steps)} emptyText={NO_OPEN_CASES} />
                {extraPie}
            </div>

            {flow && (
                <div className="flex flex-col gap-2">
                    <SectionLabel>In vs treated</SectionLabel>
                    <FlowSlot flow={flow} />
                </div>
            )}
        </OverviewCard>
    );
}

export function TeamWorkloadCardSkeleton({ team }: { team: OwnerTeam }) {
    return (
        <OverviewCard title={team}>
            <div style={{ height: SKELETON_HEIGHT_PX }}>
                <ChartSkeleton />
            </div>
        </OverviewCard>
    );
}
